// Fix for Radix UI Popover visibility issues
export function initRadixPopoverFix() {
  // Add global styles to ensure Tailwind animation classes work
  const styleEl = document.createElement('style');
  styleEl.textContent = `
    /* Radix UI animation fixes */
    @keyframes enter {
      from {
        opacity: 0;
        transform: scale(0.95);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
    
    @keyframes exit {
      from {
        opacity: 1;
        transform: scale(1);
      }
      to {
        opacity: 0;
        transform: scale(0.95);
      }
    }
    
    /* Apply animations */
    [data-state="open"] {
      animation: enter 200ms ease-out;
    }
    
    [data-state="closed"] {
      animation: exit 200ms ease-in;
    }
    
    /* Ensure transform-origin works */
    [data-slot="popover-content"],
    [data-slot="dropdown-menu-content"] {
      transform-origin: var(--radix-popper-transform-origin);
    }
  `;
  document.head.appendChild(styleEl);
  
  // Function to fix popover positioning
  const fixPopoverPosition = (wrapper: HTMLElement) => {
    const style = wrapper.getAttribute('style') || '';
    
    // If it's positioned way off-screen, reset it
    if (style.includes('translate(0px, -200%)') || style.includes('translate(-200%') || style.includes('translate(200%')) {
      // Find the trigger element
      const trigger = document.querySelector('[data-state="open"][data-slot="popover-trigger"], [data-state="open"][data-slot="dropdown-menu-trigger"]');
      
      if (trigger) {
        const rect = trigger.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        
        // Calculate position relative to trigger
        let left = rect.left;
        let top = rect.bottom + 8; // 8px gap
        
        // Ensure it stays within viewport
        if (left + wrapperRect.width > window.innerWidth) {
          left = window.innerWidth - wrapperRect.width - 10;
        }
        if (top + wrapperRect.height > window.innerHeight) {
          top = rect.top - wrapperRect.height - 8;
        }
        
        wrapper.style.transform = `translate(${left}px, ${top}px)`;
      } else {
        // Fallback: center it on screen
        wrapper.style.transform = `translate(${window.innerWidth / 2 - 150}px, ${window.innerHeight / 2 - 100}px)`;
      }
    }
  };
  
  // Watch for popovers being added to fix any CSS issues
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          // Check for Radix popper wrapper
          const wrapper = node.querySelector?.('[data-radix-popper-content-wrapper]') || 
                          (node.matches?.('[data-radix-popper-content-wrapper]') ? node : null);
          
          if (wrapper instanceof HTMLElement) {
            // Fix positioning
            setTimeout(() => fixPopoverPosition(wrapper), 0);
          }
          
          // Check for popover content
          const popoverContent = node.querySelector?.('[data-slot="popover-content"], [data-slot="dropdown-menu-content"]') || 
                                (node.matches?.('[data-slot="popover-content"], [data-slot="dropdown-menu-content"]') ? node : null);
          
          if (popoverContent instanceof HTMLElement) {
            // Ensure CSS variables are applied
            const computedStyle = getComputedStyle(popoverContent);
            if (!computedStyle.backgroundColor || computedStyle.backgroundColor === 'rgba(0, 0, 0, 0)') {
              // Apply fallback styles if CSS variables aren't working
              popoverContent.style.backgroundColor = 'white';
              popoverContent.style.color = 'black';
              popoverContent.style.boxShadow = '0 10px 15px -3px rgb(0 0 0 / 0.1)';
            }
          }
        }
      });
    });
  });

  // Start observing
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
  
  // Also fix any existing popovers
  document.querySelectorAll('[data-radix-popper-content-wrapper]').forEach((el) => {
    if (el instanceof HTMLElement) {
      fixPopoverPosition(el);
    }
  });
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRadixPopoverFix);
} else {
  initRadixPopoverFix();
}